import { useEffect, useState, useCallback, useSyncExternalStore } from 'react';
import { girderWebSocket } from '../utils/girderWebSocket';

/**
 * Hook for connecting to Girder's WebSocket notification endpoint
 * Uses singleton WebSocket manager to ensure single connection
 * @param {string} token - Girder authentication token
 * @param {string} girderUrl - Base URL for Girder (defaults to window location)
 * @returns {Object} Connection state and methods
 */
export const useGirderNotifications = (token, girderUrl = null) => {
  // Subscribe to connection state changes using useSyncExternalStore
  const connectionState = useSyncExternalStore(
    (onStoreChange) => {
      // Subscribe to state changes
      return girderWebSocket.onStateChange(onStoreChange);
    },
    () => {
      // Get current state
      return girderWebSocket.getConnectionState();
    }
  );

  // Connect when token is available, disconnect when token is removed
  useEffect(() => {
    if (token) {
      girderWebSocket.connect(token, girderUrl);
    } else {
      girderWebSocket.disconnect();
    }

    // Cleanup on unmount - but don't disconnect if other components might be using it
    // The singleton will handle cleanup when no more components need it
    // For now, we'll disconnect on unmount, but this could be improved with a ref count
    return () => {
      // Only disconnect if token is removed (not just component unmounting)
      // This prevents disconnecting when component unmounts but token still exists
      // The singleton pattern means the connection persists across component lifecycles
      // We'll let the singleton manage its own lifecycle
    };
  }, [token, girderUrl]);

  // Register a message handler - delegates to singleton
  const onMessage = useCallback((handler) => {
    return girderWebSocket.subscribe(handler);
  }, []);

  // Manual connect/disconnect methods for backward compatibility
  const connect = useCallback(() => {
    if (token) {
      girderWebSocket.connect(token, girderUrl);
    }
  }, [token, girderUrl]);

  const disconnect = useCallback(() => {
    girderWebSocket.disconnect();
  }, []);

  return {
    connectionState,
    lastError: null, // Singleton manages errors internally
    connect,
    disconnect,
    onMessage,
    isConnected: connectionState === 'connected',
  };
};
